import { useCallback, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getReceiptTypes } from "store/receipts/actions";
import useFeatureFlags from "./useFeatureFlags";

const useReceiptTypes = () => {
    const [isFeatureFlagActive] = useFeatureFlags();
    const {
        receipt_types,
        loading_receipt_types
    } = useSelector((state) => ({
        receipt_types: state.Receipts.receipt_types,
        loading_receipt_types: state.Receipts.loading_receipt_types
    }))
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(getReceiptTypes({}))
    }, [])

    const isReceiptTypeDebitActual = useCallback((receipt_type) => {
        return receipt_type?.is_debit

    }, [isFeatureFlagActive]);

    const isReceiptTypeIdDebitActual = useCallback((receipt_type_id) => {
        const receipt_type = receipt_types.find((receipt_type) => receipt_type?.id === receipt_type_id)
        return isReceiptTypeDebitActual(receipt_type)
    }, [receipt_types, isReceiptTypeDebitActual]);

    /**
     * returns a string with "<receipt type name> (Debit|Credit)"
     */
    const getReceiptNameWithDebitOrCreditForId = useCallback((receipt_type_id) => {
        const receipt_type = receipt_types.find((receipt_type) => receipt_type?.id === receipt_type_id)
        if (receipt_type) {
            return `${receipt_type.name} (${isReceiptTypeIdDebitActual(receipt_type_id) ? 'Debit' : 'Credit'})`
        }
        return '--';
    })

    /**
     * returns a string with "Debit" or "Credit"
     */
    const creditOrDebitTextByReceiptTypeId = useCallback((receipt_type_id) => {
        return isReceiptTypeIdDebitActual(receipt_type_id) ? 'Debit' : 'Credit'
    }, [isReceiptTypeIdDebitActual])

    const receipt_type_options = useMemo(() => {
        return receipt_types.map((receipt_type) => ({
            label: receipt_type.name,
            value: receipt_type.id,
            is_debit: isReceiptTypeDebitActual(receipt_type)
        }))
    }, [receipt_types])

    const getReceiptTypeById = useCallback((receipt_type_id) => {
        return receipt_types.find((receipt_type) => receipt_type?.id === receipt_type_id)
    }, [receipt_types])

    const getReceiptTypeByName = useCallback((receipt_type_name) => {
        return receipt_types.find((receipt_type) => receipt_type?.name === receipt_type_name)
    }, [receipt_types])

    const debit_receipt_type_options = useMemo(() => {
        return receipt_type_options.filter((option) => option.is_debit)
    }, [receipt_type_options])

    const credit_receipt_type_options = useMemo(() => {
        return receipt_type_options.filter((option) => !option.is_debit)
    }, [receipt_type_options])

    return {
        receipt_types,
        loading_receipt_types,
        receipt_type_options,
        debit_receipt_type_options,
        credit_receipt_type_options,
        getReceiptTypeById,
        getReceiptTypeByName,
        isReceiptTypeIdDebitActual,
        creditOrDebitTextByReceiptTypeId,
        getReceiptNameWithDebitOrCreditForId
    }
}

export default useReceiptTypes;